// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — Oracle Engine (Hybrid: Real Claude API + Mock AI Fallback)
//
// Architecture:
//   1. If VITE_CLAUDE_API_KEY is set → call real Claude API with production prompt
//   2. If key missing or call fails → fall back to MockAIOracle (NOT rigid rules)
//        - MockAIOracle generates LLM-quality narrative insights across
//          Financial, Operational, and Marketing categories
//        - Output shape is identical to real Claude output, so swapping is transparent
//
// When the user adds the API key to .env, insights automatically upgrade
// from Mock to real Claude with zero code changes.
// ══════════════════════════════════════════════════════════════════════════════

import { MockAIOracle } from './mockAIOracle';
import { SYSTEM_PROMPT, USER_PROMPT_TEMPLATE, buildPromptContext } from './promptTemplates';

const CLAUDE_API_KEY = import.meta.env.VITE_CLAUDE_API_KEY || '';
const CLAUDE_MODEL = 'claude-sonnet-4-5-20250929';
const CLAUDE_API_URL = 'https://api.anthropic.com/v1/messages';

export class SeldorOracle {
  /**
   * Main entry point. Attempts real Claude API first, falls back to Mock AI.
   * @param {Object} data - Tenant data (sales, employees, gaps, ingredients, etc.)
   * @returns {Promise<Array>} Array of insight objects
   */
  static async generateInsights(data) {
    // Real LLM path
    if (this.isUsingLLM()) {
      try {
        const llmInsights = await this.generateWithClaude(data);
        if (llmInsights && llmInsights.length > 0) {
          return llmInsights;
        }
        console.warn('[Oracle] Claude returned empty, falling back to Mock AI');
      } catch (err) {
        console.warn('[Oracle] Claude API failed, falling back to Mock AI:', err.message);
      }
    }

    // Mock AI fallback — NOT rigid rules. Generates LLM-quality narratives.
    return MockAIOracle.generate(data);
  }

  /**
   * Check whether we have a usable API key.
   */
  static isUsingLLM() {
    return Boolean(CLAUDE_API_KEY && CLAUDE_API_KEY.startsWith('sk-ant'));
  }

  /**
   * Real Claude API call using production prompt templates.
   */
  static async generateWithClaude(data) {
    const ctx = buildPromptContext(data);

    const response = await fetch(CLAUDE_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': CLAUDE_API_KEY,
        'anthropic-version': '2023-06-01'
      },
      body: JSON.stringify({
        model: CLAUDE_MODEL,
        max_tokens: 4096,
        system: SYSTEM_PROMPT,
        messages: [{ role: 'user', content: USER_PROMPT_TEMPLATE(ctx) }]
      })
    });

    if (!response.ok) {
      const errText = await response.text().catch(() => '');
      throw new Error(`Claude API ${response.status}: ${errText.slice(0, 200)}`);
    }

    const result = await response.json();
    const text = result.content?.[0]?.text || '[]';

    // Strip code fences if present
    const cleaned = text.replace(/```json|```/g, '').trim();
    const parsed = JSON.parse(cleaned);

    if (!Array.isArray(parsed)) {
      throw new Error('Claude returned non-array');
    }

    return parsed.map((p, idx) => ({
      ...p,
      id: `ai_claude_${Date.now()}_${idx}`,
      is_llm_generated: true,
      is_mock: false
    }));
  }

  /**
   * Exposed for the Insight Hub UI to display the correct badge.
   */
  static getEngineMode() {
    if (this.isUsingLLM()) return 'claude';
    return 'mock_ai';
  }
}

export default SeldorOracle;
